import { useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";

import { Images } from "../../../utils/Images";
import { TEMPLE_COLORS } from "../shared";

// Phones: bordered cards with a text +/– toggle, per the mobile design.
// panel: the temple campaign page's version (Figma "Mobile revamp" 739:17777) — questions inside a
// bordered panel, shadowed cards, a 22px toggle.
const MobileFaq = ({ content, openItems, toggleItem, panel = false }) => (
  <Box component="section" sx={{ background: "#FFFFFF", px: panel ? "16px" : "20px", pt: panel ? 0 : "76px", pb: panel ? "48px" : "76px" }}>
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      ...(panel && { border: "1px solid #EAEAF0", borderRadius: "20px", p: "20px" }),
    }}
  >
    <Typography component="h2" sx={{ fontWeight: 700, fontSize: panel ? "23px" : "24px", lineHeight: panel ? 1.5 : "32px", color: TEMPLE_COLORS.ink, textAlign: "center" }}>
      {content.title}
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: panel ? "12px" : "20px" }}>
    {content.items.map((item, index) => {
      const isOpen = openItems.includes(index);
      return (
        <Box
          key={item.question}
          sx={panel ? { background: "#FFFFFF", borderRadius: "12px", p: "16px", boxShadow: "0px 3px 14px 0px rgba(0,0,0,0.04)" } : { background: "#FFFFFF", border: "1px solid #EAEAEA", borderRadius: "12px", p: "16px" }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => toggleItem(index)}
            aria-expanded={isOpen}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              p: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
              "&:focus-visible": { outline: `2px solid ${TEMPLE_COLORS.ink}`, outlineOffset: "4px", borderRadius: "4px" },
            }}
          >
            <Typography sx={{ flex: 1, fontWeight: 700, fontSize: panel ? "14px" : "15px", lineHeight: panel ? 1.5 : "22px", color: TEMPLE_COLORS.ink }}>
              {item.question}
            </Typography>
            <Box
              sx={{
                width: panel ? 22 : 26,
                height: panel ? 22 : 26,
                flexShrink: 0,
                borderRadius: panel ? "3px" : "6px",
                background: isOpen ? (panel ? "#17CB66" : "#22C55E") : panel ? "#F1F8EB" : "#F3F4EE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontWeight: panel ? 400 : 700, fontSize: "16px", lineHeight: panel ? "normal" : "18px", color: isOpen ? "#FFFFFF" : panel ? "#6F6C90" : TEMPLE_COLORS.ink }}>
                {isOpen ? "–" : "+"}
              </Typography>
            </Box>
          </Box>
          {isOpen && (
            <Typography sx={{ mt: panel ? "12px" : "8px", fontSize: "13px", lineHeight: panel ? 1.5 : "21px", color: panel ? "#6F6C90" : TEMPLE_COLORS.muted }}>{item.answer}</Typography>
          )}
        </Box>
      );
    })}
    </Box>
  </Box>
  </Box>
);

const FaqSection = ({ content, panel = false }) => {
  const isMobile = useMediaQuery("(max-width: 899px)");
  const isPhone = useMediaQuery("(max-width: 599px)");
  const [openItems, setOpenItems] = useState([0]);

  const toggleItem = (index) => {
    setOpenItems((prev) => (prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index]));
  };

  if (isPhone) return <MobileFaq content={content} openItems={openItems} toggleItem={toggleItem} panel={panel} />;

  return (
    <Box component="section" sx={{ background: "#FFFFFF", px: isMobile ? "16px" : "40px", pt: isMobile ? "56px" : "100px", pb: isMobile ? "56px" : "100px" }}>
      <Box
        sx={{
          maxWidth: 1360,
          mx: "auto",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isMobile ? "20px" : "40px",
          px: isMobile ? 0 : "44px",
          py: isMobile ? 0 : "40px",
          background: "#FFFFFF",
          border: isMobile ? "none" : "1.022px solid #EAEAF0",
          borderRadius: "20.438px",
        }}
      >
        <Typography
          component="h2"
          sx={{ fontWeight: 700, fontSize: isMobile ? "20px" : "24px", lineHeight: 1, letterSpacing: "-0.48px", color: "#000000" }}
        >
          {content.title}
        </Typography>

        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: isMobile ? "16px" : "26px" }}>
          {content.items.map((item, index) => {
            const isOpen = openItems.includes(index);
            return (
              <Box
                key={item.question}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: "12px",
                  boxShadow: "0px 5px 16px 0px rgba(8, 15, 52, 0.06)",
                  pl: isMobile ? "16px" : "24px",
                  pr: isMobile ? "16px" : "32px",
                  py: isMobile ? "18px" : "25px",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => toggleItem(index)}
                  aria-expanded={isOpen}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                    p: 0,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                    "&:focus-visible": { outline: `2px solid ${TEMPLE_COLORS.ink}`, outlineOffset: "4px", borderRadius: "4px" },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "14px" : isOpen ? "18px" : "16px",
                      lineHeight: isMobile ? "20px" : isOpen ? "18px" : "16px",
                      color: TEMPLE_COLORS.ink,
                    }}
                  >
                    {item.question}
                  </Typography>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: isOpen ? "4px" : "2.989px",
                      background: isOpen ? "#29CC6A" : "#F5F8ED",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box component="img" src={isOpen ? Images.minusIcon : Images.plusIcon} alt="" sx={{ width: 11, height: 11 }} />
                  </Box>
                </Box>

                {isOpen && (
                  <Typography
                    sx={{
                      mt: "14px",
                      maxWidth: 1113,
                      fontSize: "14px",
                      lineHeight: "22.652px",
                      color: "#6F6C90",
                    }}
                  >
                    {item.answer}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default FaqSection;
